/**
 * Scanner application constants
 *
 * Central location for all configuration values used across scanner components.
 * This ensures consistency and makes it easy to adjust behavior without
 * modifying multiple files.
 */

/**
 * Scanner behavior constants
 */
export const SCANNER_CONSTANTS = {
  /**
   * Timeout in milliseconds for keyboard scanner input buffer
   * After this period of inactivity, the buffer is cleared
   */
  KEYBOARD_TIMEOUT_MS: 500,

  /**
   * Duration in milliseconds to display feedback messages
   * Messages will auto-dismiss after this time
   */
  FEEDBACK_DURATION_MS: 3000,

  /**
   * Duration in milliseconds to display error feedback messages
   * Error messages stay longer so user can read them
   */
  ERROR_FEEDBACK_DURATION_MS: 5000,

  /**
   * Character that marks a position/location code
   * Position codes end with this character (e.g., "A1-01@")
   */
  POSITION_MARKER: '@',

  /**
   * Delimiter character used in barcode format
   * Format: CODE#BATCH#QUANTITY
   */
  BARCODE_DELIMITER: '#',
} as const;

/**
 * Audio feedback sound file paths
 */
export const SOUND_PATHS = {
  /**
   * Sound played on successful scan/operation
   */
  SUCCESS: '/sounds/success.wav',

  /**
   * Sound played on error/failed operation
   */
  ERROR: '/sounds/bad.mp3',
} as const;

/**
 * Camera configuration constants
 */
export const CAMERA_CONSTANTS = {
  /**
   * Preferred aspect ratio for camera video
   */
  ASPECT_RATIO: 16 / 9,

  /**
   * Preferred camera facing mode
   */
  FACING_MODE: 'environment' as const,

  /**
   * Timeout for barcode detector polyfill to load (ms)
   */
  POLYFILL_TIMEOUT_MS: 10000,

  /**
   * Delay before switching camera on iOS (ms)
   */
  IOS_SWITCH_DELAY_MS: 100,

  /**
   * Fallback timeout for track stop (ms)
   */
  TRACK_STOP_TIMEOUT_MS: 500,
} as const;

/**
 * UI constants
 */
export const UI_CONSTANTS = {
  /**
   * Height of camera preview in viewport units
   */
  CAMERA_PREVIEW_HEIGHT_VH: 20,

  /**
   * Z-index for fixed bottom action bar
   */
  ACTION_BAR_Z_INDEX: 100,

  /**
   * Default empty table image type
   */
  EMPTY_IMAGE: 'simple' as const,
} as const;

/**
 * API configuration constants
 */
export const API_CONSTANTS = {
  /**
   * Timeout for API requests (ms)
   */
  REQUEST_TIMEOUT_MS: 30000,

  /**
   * Number of retry attempts for failed requests
   */
  RETRY_ATTEMPTS: 3,
} as const;

/**
 * Validation constants
 */
export const VALIDATION_CONSTANTS = {
  /**
   * Characters that are invalid in product codes
   */
  INVALID_PRODUCT_CODE_CHARS: ['#', '@'],

  /**
   * Minimum quantity value
   */
  MIN_QUANTITY: 0.01,

  /**
   * Maximum quantity value
   */
  MAX_QUANTITY: 999999,
} as const;
